import { css } from '@emotion/react';
import { InboxLocationPhonesTypes } from '@frontend/inbox-location-phones';
import { theme } from '@frontend/theme';
import { usePopoverMenu, SearchField, useFormField } from '@frontend/design-system';
import { EmptyOutboundState } from './empty-outbound-state';
import { PhoneOptions, PhoneOptionsProps } from './phone-options';
import { PopoverMenuHeader } from './popover-menu-header';

type LocationPhoneSelectionPopoverSubmenuProps<T extends PhoneOptionsProps['phones'][number]> = {
  getItemProps: ReturnType<typeof usePopoverMenu>['getItemProps'];
  hasActiveConversation?: boolean;
  onBack: () => void;
  onSelectLocationPhone: (locationPhone: string, locationId: string, threadId?: string) => void;
  phoneOptions: (InboxLocationPhonesTypes.LocationPhone & {
    threadId?: string;
  })[];
  phonesByLocation: Record<string, T[]>;
  selectedLocationPhone?: string;
  showHeader: boolean;
  title: string;
  /** When true, always shows the search field regardless of phone count */
  isNewThreadCreation?: boolean;
};

export const LocationPhoneSelectionPopoverSubmenu = <T extends PhoneOptionsProps['phones'][number]>({
  getItemProps,
  hasActiveConversation = false,
  onBack,
  onSelectLocationPhone,
  phoneOptions,
  selectedLocationPhone,
  showHeader,
  title,
  isNewThreadCreation: _isNewThreadCreation = false,
}: LocationPhoneSelectionPopoverSubmenuProps<T>) => {
  const searchField = useFormField({
    type: 'text',
  });
  const showSearch = phoneOptions.length > 5;
  const hasNoOutboundNumbers = phoneOptions.length === 0;

  if (hasNoOutboundNumbers) {
    return (
      <>
        {showHeader && <PopoverMenuHeader getItemProps={getItemProps} onBack={onBack} title={title} />}
        <EmptyOutboundState showBackButton={!showHeader && hasActiveConversation} onBack={onBack} />
      </>
    );
  }

  return (
    <>
      {showHeader && <PopoverMenuHeader getItemProps={getItemProps} onBack={onBack} title={title} />}
      {showSearch && (
        <SearchField
          {...searchField}
          name='location-phone-search'
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          containerCss={css({
            padding: showHeader ? theme.spacing(0, 2, 1) : theme.spacing(1, 2),
          })}
        />
      )}
      <div
        css={{
          maxHeight: 300,
          overflowY: 'auto',
        }}
      >
        <PhoneOptions
          phones={phoneOptions}
          getItemProps={getItemProps}
          baseIndex={1}
          onSelectLocationPhone={onSelectLocationPhone}
          activeIndex={0}
          searchText={searchField.value}
          selectedLocationPhone={selectedLocationPhone}
        />
      </div>
    </>
  );
};
